import { useState, useMemo, useEffect } from "react";
import { format, isValid } from "date-fns";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog";
import { Button } from "@/components/ui/button/button";
import { Textarea } from "@/components/ui/textarea/textarea";
import { Checkbox } from "@/components/ui/checkbox/checkbox";
import { Label } from "@/components/ui/label/label";

interface AttendanceChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
  adminName: string;
  employeeName: string;
  currentStatus: string;
  newStatus: string;
  attendanceDate: string | Date;
}

const STATUS_COLORS: Record<string, string> = {
  "Half-day": "bg-yellow-500 text-black",
  Present: "bg-green-500 text-black",
  Absent: "bg-red-500 text-white",
  "Paid Leave": "bg-cyan-500 text-black",
  "On Leave": "bg-orange-500 text-black",
  Holiday: "bg-blue-500 text-white",
  "Not Counting": "bg-gray-500 text-white",
};

export const AttendanceChangeModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  adminName,
  employeeName,
  currentStatus,
  newStatus,
  attendanceDate,
}: AttendanceChangeModalProps) => {
  const [reason, setReason] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setReason("");
      setAcknowledged(false);
    }
  }, [isOpen]);

  const canSubmit = reason.trim().length > 0 && acknowledged;

  const handleConfirm = () => {
    if (canSubmit) {
      onConfirm(reason.trim());
    }
  };

  const handleClose = () => {
    setReason("");
    setAcknowledged(false);
    onClose();
  };

  // Safe date formatting
  const formattedDate = useMemo(() => {
    if (!attendanceDate) return "Unknown date";

    try {
      const date = attendanceDate instanceof Date
        ? attendanceDate
        : new Date(attendanceDate);

      if (!isValid(date)) return "Unknown date";

      return format(date, "PPP");
    } catch {
      return "Unknown date";
    }
  }, [attendanceDate]);

  // Don't render anything if modal is not open
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl">Confirm Attendance Change</DialogTitle>
          <DialogDescription>
            Please provide a reason for changing this attendance record.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Admin Info */}
          <div className="rounded-lg bg-muted p-3">
            <p className="text-sm text-muted-foreground">You are logged in as:</p>
            <p className="font-semibold">{adminName}</p>
          </div>

          {/* Change Details */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Employee:</span>
              <span className="font-medium">{employeeName}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Date:</span>
              <span className="font-medium">{formattedDate}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status Change:</span>
              <div className="flex items-center gap-2">
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    STATUS_COLORS[currentStatus] || "bg-gray-200"
                  }`}
                >
                  {currentStatus}
                </span>
                <span className="text-muted-foreground">&rarr;</span>
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    STATUS_COLORS[newStatus] || "bg-gray-200"
                  }`}
                >
                  {newStatus}
                </span>
              </div>
            </div>
          </div>

          {/* Reason Input */}
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-sm font-medium">
              Reason for change <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="reason"
              placeholder="Enter the reason for changing this attendance..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="min-h-[100px]"
              disabled={isLoading}
            />
          </div>

          {/* Acknowledgment Checkbox */}
          <div className="flex items-start space-x-3 rounded-lg border p-3">
            <Checkbox
              id="acknowledge"
              checked={acknowledged}
              onCheckedChange={(checked) => setAcknowledged(checked as boolean)}
              disabled={isLoading}
            />
            <Label
              htmlFor="acknowledge"
              className="text-sm leading-relaxed cursor-pointer"
            >
              I understand that I am changing the attendance for{" "}
              <span className="font-semibold">{employeeName}</span> and I have a
              valid reason for this change.
            </Label>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canSubmit || isLoading}
            className="bg-primary"
          >
            {isLoading ? "Saving..." : "Confirm Change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
